/* Amares Homes — required-companion add-on on the product page.
 *
 * Adds a second product (the metal frame) alongside the bed in a single cart
 * request, so the drawer opens once with both lines rather than flickering
 * through two updates.
 *
 * Craft's <product-form> owns the submit event. Rather than fork its error
 * handling, this intercepts in the capture phase only when the box is ticked,
 * posts both items itself, and hands the response to the same cart element
 * Craft would have used. Unticked, the event passes straight through and Craft
 * behaves exactly as it always did.
 */
(function () {
  'use strict';

  function Addon(root) {
    this.root = root;
    this.check = root.querySelector('[data-amx-addon-check]');
    this.meta = root.querySelector('[data-amx-addon-meta]');
    this.priceTemplate = root.dataset.amxAddonPriceTemplate || '+ [price] for [size]';

    var payload = root.querySelector('[data-amx-addon-variants]');
    try {
      this.variants = payload ? JSON.parse(payload.textContent) : {};
    } catch (e) {
      this.variants = {};
    }

    var formId = root.dataset.amxAddonForm;
    this.form = formId ? document.getElementById(formId) : null;
    if (!this.form) this.form = root.closest('.shopify-section')?.querySelector('form[action*="/cart/add"]');
    if (!this.form || !this.check) return;

    this.current = null;
    this.busy = false;

    this.sync(this.readSelectedVariant());
    this.listen();
  }

  /**
   * Opening selection. Craft emits a selected-variant JSON blob, but only when
   * it renders a variant picker — which it skips for single-variant products.
   * The Liquid fallback covers that, so the add-on is never dead on arrival.
   */
  Addon.prototype.readSelectedVariant = function () {
    var info = this.root.closest('product-info');
    var script = info && info.querySelector('[data-selected-variant]');
    if (script) {
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        /* fall through to the attribute */
      }
    }
    var raw = this.root.dataset.amxAddonInitial;
    return raw ? { options: raw.split('~~') } : null;
  };

  /**
   * Match the bed's selection to a frame variant by *value*, not by option
   * position — the bed has Colour and Size in an order the merchant controls,
   * while the frame only has Size.
   */
  Addon.prototype.match = function (variant) {
    if (!variant) return null;
    var values = variant.options || [variant.option1, variant.option2, variant.option3];
    for (var i = 0; i < values.length; i++) {
      var key = values[i];
      if (key && Object.prototype.hasOwnProperty.call(this.variants, key)) {
        return { key: key, data: this.variants[key] };
      }
    }
    return null;
  };

  Addon.prototype.sync = function (variant) {
    var found = this.match(variant);
    this.current = found;

    if (!found) {
      // No frame in this size. Better to say so than to offer a broken tick.
      this.root.classList.add('amx-addon--unavailable');
      this.check.checked = false;
      this.check.disabled = true;
      if (this.meta) this.meta.textContent = this.root.dataset.amxAddonNoneText || 'Not available in this size';
      return;
    }

    this.root.classList.toggle('amx-addon--unavailable', !found.data.available);
    this.check.disabled = !found.data.available;
    if (!found.data.available) this.check.checked = false;

    // The price only means anything alongside the size it belongs to — the
    // frame costs a different amount for a king than for a full double.
    if (this.meta) {
      this.meta.textContent = found.data.available
        ? this.priceTemplate
            .replace('[price]', found.data.price)
            .replace('[size]', found.key)
        : found.key + ' — out of stock';
    }
  };

  Addon.prototype.listen = function () {
    var self = this;

    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        if (!event || !event.data) return;
        self.sync(event.data.variant);
      });
    }

    // Capture phase, so this runs before Craft's own submit listener.
    this.form.addEventListener(
      'submit',
      function (e) {
        if (!self.check.checked || self.check.disabled || !self.current || self.busy) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        self.submitBoth();
      },
      true
    );
  };

  Addon.prototype.submitBoth = function () {
    var self = this;
    var form = this.form;
    var data = new FormData(form);
    var bedId = data.get('id');
    var qty = parseInt(data.get('quantity'), 10) || 1;
    if (!bedId) return;

    var button = form.querySelector('[type="submit"]');
    var spinner = form.querySelector('.loading__spinner');
    if (button) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
    }
    if (spinner) spinner.classList.remove('hidden');

    var cart =
      document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    var body = {
      items: [
        { id: Number(bedId), quantity: qty },
        { id: this.current.data.id, quantity: qty },
      ],
    };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map(function (s) {
        return s.id;
      });
      body.sections_url = window.location.pathname;
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (res) {
        if (res.status) {
          // Fall back to Craft's own path so the visitor still gets the bed and
          // sees Craft's error handling rather than a dead button.
          self.recover();
          return;
        }
        if (cart) {
          cart.renderContents(res);
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        } else {
          window.location = window.routes.cart_url;
        }
      })
      .catch(function () {
        self.recover();
      })
      .finally(function () {
        if (button) {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
        }
        if (spinner) spinner.classList.add('hidden');
      });
  };

  /** Re-run the submit with the add-on stood down, letting Craft take over. */
  Addon.prototype.recover = function () {
    this.busy = true;
    if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
    else this.form.submit();
    var self = this;
    window.setTimeout(function () {
      self.busy = false;
    }, 0);
  };

  function boot(scope) {
    var roots = (scope || document).querySelectorAll('[data-amx-addon]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.amxBound) return;
      root.dataset.amxBound = '1';
      new Addon(root);
    });
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', function () { boot(); });

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
